import React, { useRef, forwardRef, useEffect, useState, useCallback } from "react";
import { Stage, Layer, Line, Image as KonvaImage, Transformer } from "react-konva";
import Konva from "konva";
import styled from "styled-components";
import Colors from "../../../ui/global/Colors";
import getStroke from "perfect-freehand";
import { Point, Object, TransformState, ObjectTypes } from "../utils/useArtHistory";
import { tools } from "./ToolbarDraw";

// Convert object points to SVG path data
const getSvgPathFromStroke = (points: Point[], closed = true) => {
    const len = points.length;
    
    if (len < 1) return '';
    
    let a = points[0];
    let b = points[0];
    const c = points[0];
    
    let result = `M${c.x},${c.y}`;
    
    for (let i = 0; i < len; i++) {
        a = points[i];
        b = points[(i + 1) % len];
        result += `L${a.x},${a.y}`;
    }
    
    if (closed) {
        result += 'Z';
    }
    
    return result;
};

// Convert perfect-freehand points to Konva line points
const getKonvaPointsFromStroke = (object: number[][]) => {
    if (!object.length) return [];

    // For Konva, we need to specify the curve type with tension with a flat array but with curve instructions
    const points: number[] = [];
    
    // Add the first point
    points.push(object[0][0], object[0][1]);
    
    // Use Catmull-Rom spline approach for smoother curves
    for (let i = 1; i < object.length; i++) {
        const curr = object[i];
        points.push(curr[0], curr[1]);
    }
    
    return points;
};

const strokeSettings = {
    thinning: 0.6,
    smoothing: 0.8,
    streamline: 0.6,
    simulatePressure: true,
}

export interface KonvaCanvasProps {
    config: any;
    imageSize: number;
    reflowStage: boolean;
    objects: Object[];
    currentStroke: Point[];
    selectedTool: string;
    selectedThickness: number;
    selectedColor: string;
    onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => void;
    onTransformEnd?: (transformState: TransformState, strokeIndex: number) => void;
    onTransformStart?: () => void;
    onImageAdd?: (dataUrl: string, transformState?: TransformState) => void;
    onToolChange?: (tool: string) => void;
}

export interface KonvaArtStageRefs {
    drawStageRef: React.RefObject<Konva.Stage>;
    outputStageRef: React.RefObject<Konva.Stage>;
    drawLayerRef: React.RefObject<Konva.Layer>;
    outputLayerRef: React.RefObject<Konva.Layer>;
    deselectObjects: () => void;
}

// Hidden file input for image uploads
const HiddenFileInput = styled.input`
    display: none;
`;

const KonvaCanvas = forwardRef<KonvaArtStageRefs, KonvaCanvasProps>(({
    config,
    imageSize,
    reflowStage,
    objects,
    currentStroke,
    selectedTool,
    selectedThickness,
    selectedColor,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onTransformEnd,
    onTransformStart,
    onImageAdd,
    onToolChange
}, ref) => {
    const REFERENCE_SIZE = config.IMAGE_SIZE;
    const drawStageRef = useRef<Konva.Stage>(null);
    const outputStageRef = useRef<Konva.Stage>(null);
    const drawLayerRef = useRef<Konva.Layer>(null);
    const outputLayerRef = useRef<Konva.Layer>(null);
    const aiImageRef = useRef<HTMLImageElement | null>(null);
    const aiImageKonvaRef = useRef<Konva.Image | null>(null);
    const transformerRef = useRef<Konva.Transformer>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    
    // State for selection
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [isTransforming, setIsTransforming] = useState(false);
    const initialTransformState = useRef<TransformState>({
        x: 0,
        y: 0,
        rotation: 0,
        scaleX: 1,
        scaleY: 1
    });

    // Method to deselect objects
    const deselectObjects = useCallback(() => {
        setSelectedId(null);
        if (transformerRef.current) {
            transformerRef.current.nodes([]);
            if (drawLayerRef.current) {
                drawLayerRef.current.batchDraw();
            }
        }
        // console.log('Objects deselected');
    }, []);

    // Expose refs to parent
    React.useImperativeHandle(ref, () => ({
        drawStageRef,
        outputStageRef,
        drawLayerRef,
        outputLayerRef,
        deselectObjects
    }));

    // Handle file input change for image upload
    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            const reader = new FileReader();
            
            reader.onload = (event) => {
                if (event.target && typeof event.target.result === 'string' && onImageAdd) {
                    const dataUrl = event.target.result;
                    
                    // Create an image element to get dimensions
                    const img = new Image();
                    img.onload = () => {
                        // Calculate scale to fit within canvas with padding
                        const padding = 20; // 20px padding
                        const maxWidth = imageSize - (padding * 2);
                        const maxHeight = imageSize - (padding * 2);
                        
                        let scale = 1;
                        let width = img.width;
                        let height = img.height;
                        
                        // Scale down if image is larger than canvas
                        if (width > maxWidth || height > maxHeight) {
                            const widthRatio = maxWidth / width;
                            const heightRatio = maxHeight / height;
                            scale = Math.min(widthRatio, heightRatio);
                            width *= scale;
                            height *= scale;
                        }
                        
                        // Create transform state with centered position
                        const transformState: TransformState = {
                            x: (imageSize - width) / 2,
                            y: (imageSize - height) / 2,
                            rotation: 0,
                            scaleX: scale,
                            scaleY: scale
                        };
                        
                        // Add image with calculated transform
                        onImageAdd(dataUrl, transformState);
                        
                        // Switch to select tool
                        if (onToolChange) {
                            onToolChange(tools.select);
                        }
                    };
                    img.src = dataUrl;
                }
            };
            
            reader.readAsDataURL(file);
            
            // Reset the input value so the same file can be selected again
            e.target.value = '';
        }
    };

    // Handle image tool click
    useEffect(() => {
        if (selectedTool === tools.image && fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, [selectedTool]);

    // Handle paste events for images
    useEffect(() => {
        const handlePaste = (e: ClipboardEvent) => {
            if (e.clipboardData) {
                const items = e.clipboardData.items;
                
                for (let i = 0; i < items.length; i++) {
                    if (items[i].type.indexOf('image') !== -1) {
                        const blob = items[i].getAsFile();
                        if (blob && onImageAdd) {
                            // Deselect any previously selected objects
                            deselectObjects();
                            
                            const reader = new FileReader();
                            reader.onload = (event) => {
                                if (event.target && typeof event.target.result === 'string') {
                                    const dataUrl = event.target.result;
                                    
                                    // Create an image element to get dimensions
                                    const img = new Image();
                                    img.onload = () => {
                                        // Calculate scale to fit within canvas with padding
                                        const padding = 20; // 20px padding
                                        const maxWidth = imageSize - (padding * 2);
                                        const maxHeight = imageSize - (padding * 2);
                                        
                                        let scale = 1;
                                        let width = img.width;
                                        let height = img.height;
                                        
                                        // Scale down if image is larger than canvas
                                        if (width > maxWidth || height > maxHeight) {
                                            const widthRatio = maxWidth / width;
                                            const heightRatio = maxHeight / height;
                                            scale = Math.min(widthRatio, heightRatio);
                                            width *= scale;
                                            height *= scale;
                                        }
                                        
                                        // Create transform state with centered position
                                        const transformState: TransformState = {
                                            x: (imageSize - width) / 2,
                                            y: (imageSize - height) / 2,
                                            rotation: 0,
                                            scaleX: scale,
                                            scaleY: scale
                                        };
                                        
                                        // Add image with calculated transform
                                        onImageAdd(dataUrl, transformState);
                                        
                                        // The newly added image will be the last object in the objects array
                                        // We need to select it after it's added
                                        setTimeout(() => {
                                            // Select the newly added object (it will be the last one)
                                            const newIndex = objects.length;
                                            setSelectedId(`object-${newIndex}`);
                                        }, 0);
                                        
                                        // Switch to select tool
                                        if (onToolChange) {
                                            onToolChange(tools.select);
                                        }
                                    };
                                    img.src = dataUrl;
                                }
                            };
                            reader.readAsDataURL(blob);
                        }
                        break;
                    }
                }
            }
        };
        
        document.addEventListener('paste', handlePaste);
        
        return () => {
            document.removeEventListener('paste', handlePaste);
        };
    }, [onImageAdd, imageSize, onToolChange, deselectObjects, objects.length]);

    // Update transformer when selection changes
    useEffect(() => {
        if (!transformerRef.current || !drawLayerRef.current) return;
        
        const transformer = transformerRef.current;
        const stage = drawStageRef.current;
        const layer = drawLayerRef.current;
        
        if (selectedId) {
            // Find the selected node
            const selectedNode = layer.findOne(`#${selectedId}`);
            
            if (selectedNode) {
                // Store initial transform state for history
                initialTransformState.current = {
                    x: selectedNode.x(),
                    y: selectedNode.y(),
                    rotation: selectedNode.rotation(),
                    scaleX: selectedNode.scaleX(),
                    scaleY: selectedNode.scaleY()
                };
                
                // Attach transformer to the selected node
                transformer.nodes([selectedNode]);
                transformer.visible(true);
                layer.batchDraw();
            } else {
                // If node not found, clear selection
                transformer.nodes([]);
                transformer.visible(false);
                layer.batchDraw();
            }
        } else {
            // Clear selection
            transformer.nodes([]);
            transformer.visible(false);
            layer.batchDraw();
        }
    }, [selectedId]);

    // Clear selection when switching to a non-cursor tool
    useEffect(() => {
        if (selectedTool !== tools.select) {
            setSelectedId(null);
        }
    }, [selectedTool]);

    // Handle click on empty area to deselect
    const checkDeselect = (e: Konva.KonvaEventObject<MouseEvent>) => {
        if (selectedTool !== tools.select) return;
        
        // Clicked on an empty area
        const clickedOnEmpty = e.target === e.target.getStage();
        if (clickedOnEmpty) {
            setSelectedId(null);
        }
    };

    // Custom event handlers for selection mode
    const handleStageMouseDown = (e: Konva.KonvaEventObject<MouseEvent>) => {
        if (selectedTool === tools.select) {
            checkDeselect(e);
        } else {
            // Use the original pointer down handler for drawing
            onPointerDown(e as unknown as React.PointerEvent<HTMLDivElement>);
        }
    };

    const handleStageMouseMove = (e: Konva.KonvaEventObject<MouseEvent>) => {
        if (selectedTool !== tools.select) {
            // Use the original pointer move handler for drawing
            onPointerMove(e as unknown as React.PointerEvent<HTMLDivElement>);
        }
    };

    const handleStageMouseUp = () => {
        if (selectedTool !== tools.select) {
            // Use the original pointer up handler for drawing
            onPointerUp(undefined as unknown as React.PointerEvent<HTMLDivElement>);
        } else if (isTransforming) {
            // If we were transforming, end the transform
            setIsTransforming(false);
            
            // Notify parent component that a transform has ended
            if (onTransformEnd) {
                onTransformEnd(initialTransformState.current, -1);
            }
        }
    };

    // Handle transform start
    const handleTransformStart = () => {
        setIsTransforming(true);
        
        // Store initial position for history
        if (selectedId) {
            const node = drawLayerRef.current?.findOne(`#${selectedId}`);
            if (node) {
                // Store raw values
                const rawTransform = {
                    x: node.x(),
                    y: node.y(),
                    rotation: node.rotation(),
                    scaleX: node.scaleX(),
                    scaleY: node.scaleY()
                };
                
                // console.log('Transform start - raw values:', {
                //     ...rawTransform,
                //     scaleFactor,
                //     REFERENCE_SIZE
                // });
                
                // Store values adjusted to reference size
                initialTransformState.current = {
                    x: node.x() / scaleFactor,
                    y: node.y() / scaleFactor,
                    rotation: node.rotation(),
                    scaleX: node.scaleX(),
                    scaleY: node.scaleY()
                };
                
                // console.log('Transform start - adjusted for reference size:', initialTransformState.current);
            }
        }
        
        // Call onTransformStart if provided
        if (onTransformStart) {
            onTransformStart();
        }
    };

    // Handle drag end
    const handleDragEnd = (e: Konva.KonvaEventObject<DragEvent>) => {
        const node = e.target;
        const strokeIndex = parseInt(node.id().split('-')[1]);
        
        // Get current transform state
        const currentTransform: TransformState = {
            x: node.x(),
            y: node.y(),
            rotation: node.rotation(),
            scaleX: node.scaleX(),
            scaleY: node.scaleY()
        };
        
        // console.log('Drag end - raw values:', {
        //     x: node.x(),
        //     y: node.y(),
        //     scaleFactor,
        //     REFERENCE_SIZE
        // });
        
        // The transform coordinates need to be converted back to reference size coordinates before being stored in history
        const referenceTransform: TransformState = {
            x: node.x() / scaleFactor,
            y: node.y() / scaleFactor,
            rotation: node.rotation(),
            scaleX: node.scaleX(),
            scaleY: node.scaleY()
        };
        
        // console.log('Drag end - adjusted for reference size:', referenceTransform);
        
        // Call onTransformEnd with the transform state and object index
        if (onTransformEnd) {
            onTransformEnd(referenceTransform, strokeIndex);
        }
    };

    // Handle transform end
    const handleTransformEnd = (e: Konva.KonvaEventObject<Event>) => {
        setIsTransforming(false);
        const node = e.target;
        const strokeIndex = parseInt(node.id().split('-')[1]);
        
        // Get current transform state
        const currentTransform: TransformState = {
            x: node.x(),
            y: node.y(),
            rotation: node.rotation(),
            scaleX: node.scaleX(),
            scaleY: node.scaleY()
        };
        
        // console.log('Transform end - raw values:', {
        //     x: node.x(),
        //     y: node.y(),
        //     scaleFactor,
        //     REFERENCE_SIZE
        // });
        
        // The transform coordinates need to be converted back to reference size coordinates
        // before being stored in history
        const referenceTransform: TransformState = {
            x: node.x() / scaleFactor,
            y: node.y() / scaleFactor,
            rotation: node.rotation(),
            scaleX: node.scaleX(),
            scaleY: node.scaleY()
        };
        
        // console.log('Transform end - adjusted for reference size:', referenceTransform);
        
        // Call onTransformEnd with the transform state and object index
        if (onTransformEnd) {
            onTransformEnd(referenceTransform, strokeIndex);
        }
    };

    // Handle stage resizing when imageSize changes
    useEffect(() => {
        if (drawStageRef.current) {
            // Update stage dimensions
            drawStageRef.current.width(imageSize);
            drawStageRef.current.height(imageSize);

            // console.log('Konva size: ', drawStageRef.current.width(), drawStageRef.current.height());
            
            // Force a redraw
            drawStageRef.current.batchDraw();
        }
    }, [imageSize]);

    // useEffect(() => {
    //     console.log('Konva image size: ', imageSize);
    // }, [imageSize]);
    
    const scaleFactor = imageSize / REFERENCE_SIZE;

    // Handle keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            
            // Escape key to deselect
            if (e.key === 'Escape') {
                deselectObjects();
                return;
            }
            
            // Delete or backspace key to delete selected object
            if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
                // Extract the index from the selectedId (format: 'object-{index}')
                const selectedIndex = parseInt(selectedId.split('-')[1]);
                
                // Validate the index
                if (!isNaN(selectedIndex) && selectedIndex >= 0 && selectedIndex < objects.length) {
                    // Create a new array without the selected object
                    const updatedObjects = [...objects];
                    updatedObjects.splice(selectedIndex, 1);
                    
                    // Trigger transform end to update art history
                    if (onTransformEnd) {
                        // Create a dummy transform state to indicate deletion
                        const deleteTransform = {
                            x: 0,
                            y: 0,
                            rotation: 0,
                            scaleX: 0,
                            scaleY: 0
                        };
                        
                        onTransformEnd(deleteTransform, selectedIndex);
                    }
                    
                    // Deselect after deletion
                    deselectObjects();
                }
            }
        };
        
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [deselectObjects, selectedId, objects, onTransformEnd, onImageAdd, scaleFactor]);
    
    // Handle clicks outside the canvas to deselect objects
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            // If we have a stage reference and we're in select mode
            if (drawStageRef.current && selectedTool === tools.select) {
                // Get the DOM element of the stage container
                const stageContainer = drawStageRef.current.container();
                
                // Check if the click was outside the stage container
                if (!stageContainer.contains(e.target as Node)) {
                    deselectObjects();
                }
            }
        };
        
        // Add event listener to document
        document.addEventListener('mousedown', handleClickOutside);
        
        // Clean up event listener on unmount
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [deselectObjects, selectedTool]);
    
    return (
        <>
            <HiddenFileInput 
                type="file" 
                ref={fileInputRef} 
                accept="image/*" 
                onChange={handleFileInputChange}
            />
            <Stage 
                ref={drawStageRef}
                width={imageSize} 
                height={imageSize}
                scaleX={1}
                scaleY={1}
                style={{ 
                    touchAction: 'none', // Prevent browser touch actions like scrolling
                    cursor: selectedTool === tools.select ? 'default' : 'crosshair',
                }}
                onTouchStart={(e) => {
                    // Type safety: cast to unknown first
                    checkDeselect(e as unknown as Konva.KonvaEventObject<MouseEvent>);
                    // Convert touch event to pointer event for drawing
                    if (onPointerDown && selectedTool !== tools.select) {
                        const evt = e.evt;
                        const stage = e.target.getStage();
                        if (stage) {
                            const pos = stage.getPointerPosition();
                            if (pos && evt) {
                                const container = stage.container();
                                // Create a synthetic pointer event
                                const syntheticEvent = {
                                    preventDefault: () => evt.preventDefault(),
                                    clientX: pos.x,
                                    clientY: pos.y,
                                    currentTarget: container,
                                    target: container,
                                    pressure: 1.0
                                } as unknown as React.PointerEvent<HTMLDivElement>;
                                onPointerDown(syntheticEvent);
                            }
                        }
                    }
                }}
                onTouchMove={(e) => {
                    // Convert touch event to pointer event for drawing
                    if (onPointerMove && selectedTool !== tools.select) {
                        const evt = e.evt;
                        const stage = e.target.getStage();
                        if (stage) {
                            const pos = stage.getPointerPosition();
                            if (pos && evt) {
                                const container = stage.container();
                                // Create a synthetic pointer event
                                const syntheticEvent = {
                                    preventDefault: () => evt.preventDefault(),
                                    clientX: pos.x,
                                    clientY: pos.y,
                                    currentTarget: container,
                                    target: container,
                                    pressure: 1.0
                                } as unknown as React.PointerEvent<HTMLDivElement>;
                                onPointerMove(syntheticEvent);
                            }
                        }
                    }
                }}
                onTouchEnd={(e) => {
                    // Convert touch event to pointer event for drawing
                    if (onPointerUp && selectedTool !== tools.select) {
                        const evt = e.evt;
                        const stage = e.target.getStage();
                        if (stage) {
                            const pos = stage.getPointerPosition();
                            if (pos && evt) {
                                const container = stage.container();
                                // Create a synthetic pointer event
                                const syntheticEvent = {
                                    preventDefault: () => evt.preventDefault(),
                                    clientX: pos.x,
                                    clientY: pos.y,
                                    currentTarget: container,
                                    target: container,
                                    pressure: 1.0
                                } as unknown as React.PointerEvent<HTMLDivElement>;
                                onPointerUp(syntheticEvent);
                            }
                        }
                    }
                }}
                onPointerDown={(e) => {
                    if (selectedTool !== tools.select && onPointerDown) {
                        // Only handle pointer events on desktop browsers
                        if (!('ontouchstart' in window)) {
                            onPointerDown(e as unknown as React.PointerEvent<HTMLDivElement>);
                        }
                    }
                }}
                onPointerMove={(e) => {
                    if (selectedTool !== tools.select && onPointerMove) {
                        // Only handle pointer events on desktop browsers
                        if (!('ontouchstart' in window)) {
                            onPointerMove(e as unknown as React.PointerEvent<HTMLDivElement>);
                        }
                    }
                }}
                onPointerUp={(e) => {
                    if (selectedTool !== tools.select && onPointerUp) {
                        // Only handle pointer events on desktop browsers
                        if (!('ontouchstart' in window)) {
                            onPointerUp(e as unknown as React.PointerEvent<HTMLDivElement>);
                        }
                    }
                }}
            >
                <Layer ref={drawLayerRef}>
                    {/* Render completed objects */}
                    {objects.map((object, index) => {
                        // Handle image objects
                        if (object.type === 'image' && object.data.imageData) {
                            const imageObj = new window.Image();
                            imageObj.src = object.data.imageData as string;
                            
                            // Ensure transform property exists
                            const transform = object.transform || {
                                x: 0,
                                y: 0,
                                rotation: 0,
                                scaleX: 1,
                                scaleY: 1
                            };
                            
                            return (
                                <KonvaImage
                                    key={index}
                                    id={`object-${index}`}
                                    image={imageObj}
                                    x={transform.x * scaleFactor}
                                    y={transform.y * scaleFactor}
                                    rotation={transform.rotation}
                                    scaleX={transform.scaleX}
                                    scaleY={transform.scaleY}
                                    draggable={selectedTool === tools.select}
                                    onClick={(e) => {
                                        if (selectedTool === tools.select) {
                                            // Prevent bubbling to stage
                                            e.cancelBubble = true;
                                            setSelectedId(`object-${index}`);
                                        }
                                    }}
                                    onTap={(e) => {
                                        if (selectedTool === tools.select) {
                                            // Prevent bubbling to stage
                                            e.cancelBubble = true;
                                            setSelectedId(`object-${index}`);
                                        }
                                    }}
                                    onDragStart={(e) => {
                                        // Store initial position for history
                                        const node = e.target;
                                        setSelectedId(`object-${index}`);
                                        initialTransformState.current = {
                                            x: node.x(),
                                            y: node.y(),
                                            rotation: node.rotation(),
                                            scaleX: node.scaleX(),
                                            scaleY: node.scaleY()
                                        };
                                    }}
                                    onDragEnd={handleDragEnd}
                                    onTransformEnd={handleTransformEnd}
                                    onMouseEnter={(e) => {
                                        if (selectedTool === tools.select) {
                                            // Change cursor to move cursor when hovering over a selectable object
                                            const stage = e.target.getStage();
                                            if (stage) {
                                                stage.container().style.cursor = 'move';
                                            }
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (selectedTool === tools.select) {
                                            // Reset cursor when leaving the object
                                            const stage = e.target.getStage();
                                            if (stage) {
                                                stage.container().style.cursor = 'default';
                                            }
                                        }
                                    }}
                                />
                            );
                        }
                        
                        // Handle stroke and fill objects
                        if (object.type === 'stroke' && object.data.points && object.data.points.length > 0) {
                            let points;
                            if (object.data.isFill) {
                                points = object.data.points.map(point => [point.x, point.y]).flat();
                            } else {
                                const perfectStroke = getStroke(object.data.points, {
                                    size: object.data.thickness,
                                    ...strokeSettings
                                });
                                points = getKonvaPointsFromStroke(perfectStroke);
                            }
                            
                            // Ensure transform property exists
                            const transform = object.transform || {
                                x: 0,
                                y: 0,
                                rotation: 0,
                                scaleX: 1,
                                scaleY: 1
                            };
                            
                            return (
                                <Line
                                    key={index}
                                    id={`object-${index}`}
                                    points={points.map((point, i) => i % 2 === 0 ? point * scaleFactor : point * scaleFactor)}
                                    fill={object.data.color}
                                    closed={true}
                                    opacity={0.9}
                                    perfectDrawEnabled={true}
                                    tension={0.5}
                                    bezier={true}
                                    draggable={selectedTool === tools.select}
                                    x={transform.x * scaleFactor}
                                    y={transform.y * scaleFactor}
                                    rotation={transform.rotation}
                                    scaleX={transform.scaleX}
                                    scaleY={transform.scaleY}
                                    onClick={(e) => {
                                        if (selectedTool === tools.select) {
                                            // Prevent bubbling to stage
                                            e.cancelBubble = true;
                                            setSelectedId(`object-${index}`);
                                        }
                                    }}
                                    onTap={(e) => {
                                        if (selectedTool === tools.select) {
                                            // Prevent bubbling to stage
                                            e.cancelBubble = true;
                                            setSelectedId(`object-${index}`);
                                        }
                                    }}
                                    onDragStart={(e) => {
                                        // Store initial position for history
                                        const node = e.target;
                                        setSelectedId(`object-${index}`);
                                        initialTransformState.current = {
                                            x: node.x(),
                                            y: node.y(),
                                            rotation: node.rotation(),
                                            scaleX: node.scaleX(),
                                            scaleY: node.scaleY()
                                        };
                                    }}
                                    onDragEnd={handleDragEnd}
                                    onTransformEnd={handleTransformEnd}
                                    onMouseEnter={(e) => {
                                        if (selectedTool === tools.select) {
                                            // Change cursor to move cursor when hovering over a selectable object
                                            const stage = e.target.getStage();
                                            if (stage) {
                                                stage.container().style.cursor = 'move';
                                            }
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (selectedTool === tools.select) {
                                            // Reset cursor when leaving the object
                                            const stage = e.target.getStage();
                                            if (stage) {
                                                stage.container().style.cursor = 'default';
                                            }
                                        }
                                    }}
                                />
                            );
                        }
                        
                        return null;
                    })}
                    
                    {/* Render current stroke */}
                    {currentStroke.length > 0 && (
                        selectedTool === tools.fill ? (
                            <Line
                                points={currentStroke.length > 1 ? 
                                    [...currentStroke.flatMap(point => [point.x * scaleFactor, point.y * scaleFactor]), 
                                     currentStroke[0].x * scaleFactor, currentStroke[0].y * scaleFactor] :
                                    currentStroke.flatMap(point => [point.x * scaleFactor, point.y * scaleFactor])
                                }
                                fill={selectedColor}
                                closed={true}
                                opacity={0.9}
                                perfectDrawEnabled={true}
                                tension={0.5}
                                bezier={true}
                                listening={false}
                            />
                        ) : selectedTool === tools.draw && (
                            <Line
                                points={currentStroke.flatMap(point => [
                                    point.x * scaleFactor, 
                                    point.y * scaleFactor
                                ])}
                                stroke={selectedColor}
                                strokeWidth={selectedThickness * scaleFactor}
                                lineCap="round"
                                lineJoin="round"
                                tension={0.5}
                            />
                        )
                    )}
                    
                    {/* Transformer for selection */}
                    <Transformer
                        ref={transformerRef}
                        visible={false}
                        boundBoxFunc={(oldBox, newBox) => {
                            // Limit minimum size
                            if (newBox.width < 5 || newBox.height < 5) {
                                return oldBox;
                            }
                            
                            // Limit maximum size to stage dimensions
                            if (newBox.width > imageSize || newBox.height > imageSize) {
                                return oldBox;
                            }
                            
                            return newBox;
                        }}
                        rotateEnabled={true}
                        rotationSnaps={Array.from({ length: 8 }, (_, i) => i * 45)}
                        resizeEnabled={true}
                        enabledAnchors={[
                            'top-left', 'top-right',
                            'bottom-left', 'bottom-right'
                        ]}
                        anchorStroke={Colors.white}
                        anchorFill={Colors.black}
                        anchorSize={10}
                        anchorCornerRadius={3}
                        borderStroke={Colors.black}
                        borderStrokeWidth={1}
                        borderDash={[]}
                        keepRatio={true}
                        padding={0}
                        rotateLineVisible={false}
                        rotateAnchorOffset={20}
                        onTransformStart={handleTransformStart}
                        onTransformEnd={handleTransformEnd}
                    />
                </Layer>
            </Stage>
        </>
    );
});

export default KonvaCanvas;
